import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCsrfToken } from '../../Hooks/useCsrfToken';
import RosterAiTradeIdeasModal from '../../Roster/RosterAiTradeIdeasModal/RosterAiTradeIdeasModal';

const LeagueRostersCard = ({
	rosterId,
	getLeagueRostersEndpoint,
	leagueRostersUpdated,
	getRosterAiTradeIdeasEndpoint,
	isScraping = false,
	currentRosterTeamName = null
}) => {
	const csrfToken = useCsrfToken();
	const [
		leagueRosters,
		setLeagueRosters
	] = useState([]);
	const [
		loading,
		setLoading
	] = useState(true);
	const [
		error,
		setError
	] = useState(null);
	const [
		tradeIdeasModalOpen,
		setTradeIdeasModalOpen
	] = useState(false);
	const [
		tradeIdeasLoading,
		setTradeIdeasLoading
	] = useState(false);
	const [
		tradeIdeas,
		setTradeIdeas
	] = useState([]);
	const [
		selectedLeagueRosterId,
		setSelectedLeagueRosterId
	] = useState(null);
	const [
		modalStep,
		setModalStep
	] = useState('type-selection'); // 'type-selection', 'player-selection', or 'results'
	const [
		userRosterPlayers,
		setUserRosterPlayers
	] = useState([]);
	const [
		leagueRosterPlayers,
		setLeagueRosterPlayers
	] = useState([]);
	const [
		selectedPlayers,
		setSelectedPlayers
	] = useState([]);
	const [
		conversationHistory,
		setConversationHistory
	] = useState([]);

	useEffect(
		() => {
			// Don't fetch if scraping is in progress
			if (!isScraping && csrfToken) {
				fetchLeagueRosters();
			}
		},
		[
			rosterId,
			leagueRostersUpdated,
			isScraping,
			csrfToken
		]
	);

	const fetchLeagueRosters = async () => {
		try {
			console.log('=== FETCHING LEAGUE ROSTERS ===');
			console.log('Roster ID:', rosterId);
			console.log(
				'Endpoint:',
				getLeagueRostersEndpoint.replace('roster_id', rosterId)
			);

			setLoading(true);

			const response = await fetch(
				getLeagueRostersEndpoint.replace('roster_id', rosterId),
				{
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					}
				}
			);

			console.log('Response status:', response.status);
			const data = await response.json();
			console.log('Raw API response:', data);

			if (data.data) {
				console.log('=== PROCESSING LEAGUE ROSTERS DATA ===');
				console.log('Total league rosters received:', data.data.length);

				// Log each league roster's draft picks
				data.data.forEach((lr, index) => {
					console.log(`League Roster ${index + 1}:`, {
						team_name: lr.team_name,
						draft_picks: lr.draft_picks,
						has_draft_picks: !!lr.draft_picks,
						draft_picks_type: typeof lr.draft_picks
					});
				});

				// Sort rosters so the user's roster appears last
				const sortedRosters = data.data.sort((a, b) => {
					// If one is the user's roster, put it last
					if (currentRosterTeamName && a.team_name === currentRosterTeamName)
						return 1;
					if (currentRosterTeamName && b.team_name === currentRosterTeamName)
						return -1;
					// Otherwise maintain original order
					return 0;
				});

				console.log('=== SETTING LEAGUE ROSTERS STATE ===');
				console.log('Sorted rosters:', sortedRosters);
				setLeagueRosters(sortedRosters);
			} else {
				console.error('No data in response:', data);
				setError(data.message || 'Failed to fetch league rosters');
			}
		} catch (err) {
			console.error('Error fetching league rosters:', err);
			setError('Error fetching league rosters');
		} finally {
			setLoading(false);
		}
	};

	const handleAiTradeIdeasClick = async (leagueRosterId) => {
		// Reset state and open modal to type selection
		setSelectedLeagueRosterId(leagueRosterId);
		setModalStep('type-selection');
		setTradeIdeas([]);
		setSelectedPlayers([]);
		setTradeIdeasModalOpen(true);

		// Fetch user roster players AND league roster players for player selection steps
		try {
			// Find the user's league roster from the leagueRosters array
			const userLeagueRoster = leagueRosters.find(
				(lr) => currentRosterTeamName && lr.team_name === currentRosterTeamName
			);

			if (userLeagueRoster) {
				const allPlayers = [
					...(userLeagueRoster.quarterbacks || []),
					...(userLeagueRoster.running_backs || []),
					...(userLeagueRoster.wide_receivers || []),
					...(userLeagueRoster.tight_ends || [])
				];
				setUserRosterPlayers(allPlayers);
			}

			// Find the selected league roster for trade-for mode
			const targetLeagueRoster = leagueRosters.find(
				(lr) => lr.id === leagueRosterId
			);

			if (targetLeagueRoster) {
				const leaguePlayers = [
					...(targetLeagueRoster.quarterbacks || []),
					...(targetLeagueRoster.running_backs || []),
					...(targetLeagueRoster.wide_receivers || []),
					...(targetLeagueRoster.tight_ends || [])
				];
				setLeagueRosterPlayers(leaguePlayers);
			}
		} catch (err) {
			console.error('Error fetching roster players:', err);
		}
	};

	const handleTradeTypeSelect = async (tradeType) => {
		if (tradeType === 'generate') {
			// Original flow: generate 3 trades automatically
			setModalStep('results');
			setTradeIdeasLoading(true);

			try {
				const endpoint =
					getRosterAiTradeIdeasEndpoint.replace('roster_id', rosterId) +
					`?league_roster_id=${selectedLeagueRosterId}`;

				const response = await fetch(endpoint, {
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					}
				});

				if (response.ok) {
					const data = await response.json();

					// Parse the AI response and extract trade ideas
					if (data.trade_idea_1 || data.trade_idea_2 || data.trade_idea_3) {
						const ideas = [];
						if (data.trade_idea_1) ideas.push(data.trade_idea_1);
						if (data.trade_idea_2) ideas.push(data.trade_idea_2);
						if (data.trade_idea_3) ideas.push(data.trade_idea_3);
						setTradeIdeas(ideas);
					} else {
						setTradeIdeas([]);
					}
				} else {
					console.error('Failed to fetch trade ideas');
					setTradeIdeas([]);
				}
			} catch (err) {
				console.error('Error fetching trade ideas:', err);
				setTradeIdeas([]);
			} finally {
				setTradeIdeasLoading(false);
			}
		} else if (tradeType === 'targeted') {
			// Trade-away flow: show player selection from user roster
			setModalStep('player-selection');
		} else if (tradeType === 'trade-for') {
			// Trade-for flow: show player selection from league roster
			setModalStep('league-player-selection');
		} else if (tradeType === 'trade-analyzer') {
			// Trade analyzer flow: show player selection from user roster first
			setModalStep('trade-analyzer-your-side');
		} else if (tradeType === 'trade-analyzer-their-side-internal') {
			// Trade analyzer step 2: show player selection from league roster
			setModalStep('trade-analyzer-their-side');
		} else if (tradeType === 'chat') {
			// Chat mode: show chat interface
			setModalStep('chat');
			setConversationHistory([]);
		}
	};

	const handlePlayerSelectionComplete = async (selectedPlayerNames) => {
		setSelectedPlayers(selectedPlayerNames);
		setModalStep('results');
		setTradeIdeasLoading(true);

		try {
			const endpoint =
				'/api/v1/rosters/' + rosterId + '/get_roster_ai_targeted_trade_ideas';

			const response = await fetch(endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					league_roster_id: selectedLeagueRosterId,
					selected_players: selectedPlayerNames
				})
			});

			if (response.ok) {
				const data = await response.json();

				// Parse the AI response and extract trade ideas
				if (data.trade_idea_1 || data.trade_idea_2 || data.trade_idea_3) {
					const ideas = [];
					if (data.trade_idea_1) ideas.push(data.trade_idea_1);
					if (data.trade_idea_2) ideas.push(data.trade_idea_2);
					if (data.trade_idea_3) ideas.push(data.trade_idea_3);
					setTradeIdeas(ideas);
				} else {
					setTradeIdeas([]);
				}
			} else {
				console.error('Failed to fetch targeted trade ideas');
				setTradeIdeas([]);
			}
		} catch (err) {
			console.error('Error fetching targeted trade ideas:', err);
			setTradeIdeas([]);
		} finally {
			setTradeIdeasLoading(false);
		}
	};

	const handleLeaguePlayerSelectionComplete = async (targetPlayerNames) => {
		setSelectedPlayers(targetPlayerNames);
		setModalStep('results');
		setTradeIdeasLoading(true);

		try {
			const endpoint =
				'/api/v1/rosters/' + rosterId + '/get_roster_ai_trade_for_players';

			const response = await fetch(endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					league_roster_id: selectedLeagueRosterId,
					target_players: targetPlayerNames
				})
			});

			if (response.ok) {
				const data = await response.json();

				// Parse the AI response and extract trade ideas
				if (data.trade_idea_1 || data.trade_idea_2 || data.trade_idea_3) {
					const ideas = [];
					if (data.trade_idea_1) ideas.push(data.trade_idea_1);
					if (data.trade_idea_2) ideas.push(data.trade_idea_2);
					if (data.trade_idea_3) ideas.push(data.trade_idea_3);
					setTradeIdeas(ideas);
				} else {
					setTradeIdeas([]);
				}
			} else {
				console.error('Failed to fetch trade-for ideas');
				setTradeIdeas([]);
			}
		} catch (err) {
			console.error('Error fetching trade-for ideas:', err);
			setTradeIdeas([]);
		} finally {
			setTradeIdeasLoading(false);
		}
	};

	const handleTradeAnalyzerComplete = async (
		yourSidePlayers,
		theirSidePlayers
	) => {
		setModalStep('results');
		setTradeIdeasLoading(true);

		try {
			const endpoint =
				'/api/v1/rosters/' + rosterId + '/get_roster_ai_trade_analyzer';

			const response = await fetch(endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					league_roster_id: selectedLeagueRosterId,
					your_side_players: yourSidePlayers,
					their_side_players: theirSidePlayers
				})
			});

			if (response.ok) {
				const data = await response.json();

				// Parse the AI response and extract trade analysis
				if (data.trade_idea_1 || data.trade_idea_2 || data.trade_idea_3) {
					const ideas = [];
					if (data.trade_idea_1) ideas.push(data.trade_idea_1);
					if (data.trade_idea_2) ideas.push(data.trade_idea_2);
					if (data.trade_idea_3) ideas.push(data.trade_idea_3);
					setTradeIdeas(ideas);
				} else {
					setTradeIdeas([]);
				}
			} else {
				console.error('Failed to fetch trade analyzer results');
				setTradeIdeas([]);
			}
		} catch (err) {
			console.error('Error fetching trade analyzer results:', err);
			setTradeIdeas([]);
		} finally {
			setTradeIdeasLoading(false);
		}
	};

	const handleSendChatMessage = async (userMessage) => {
		// Add user message to history immediately
		const newUserMessage = { role: 'user', content: userMessage };
		const updatedHistory = [
			...conversationHistory,
			newUserMessage
		];
		setConversationHistory(updatedHistory);
		setTradeIdeasLoading(true);

		try {
			const endpoint =
				'/api/v1/rosters/' + rosterId + '/get_roster_ai_chat_response';

			const response = await fetch(endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					league_roster_id: selectedLeagueRosterId,
					user_question: userMessage,
					conversation_history: conversationHistory
				})
			});

			if (response.ok) {
				const data = await response.json();

				if (data.success && data.response) {
					// Add AI response to history
					const aiMessage = { role: 'assistant', content: data.response };
					setConversationHistory((prev) => [
						...prev,
						aiMessage
					]);
				} else {
					console.error('Failed to get AI chat response');
				}
			} else {
				console.error('Failed to fetch AI chat response');
			}
		} catch (err) {
			console.error('Error in chat:', err);
		} finally {
			setTradeIdeasLoading(false);
		}
	};

	const handleCloseTradeIdeasModal = () => {
		setTradeIdeasModalOpen(false);
		setTradeIdeas([]);
		setSelectedLeagueRosterId(null);
		setModalStep('type-selection');
		setSelectedPlayers([]);
		setConversationHistory([]);
	};

	const renderPlayers = (players, position) => {
		if (!players || players.length === 0) {
			return (
				<div className="text-gray-400 dark:text-gray-500 text-sm italic bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
					No {position}
				</div>
			);
		}

		// Map position abbreviations to full position names
		const positionMap = {
			QBs: 'quarterback',
			RBs: 'running back',
			WRs: 'wide receiver',
			TEs: 'tight end'
		};

		const fullPosition = positionMap[position] || position;

		return (
			<div className="space-y-2">
				{players.map((player, index) => {
					const linkUrl = `/players/${player.id}?position=${encodeURIComponent(
						fullPosition
					)}`;

					return (
						<a
							key={index}
							href={linkUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="block text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors bg-gray-50 dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-700 hover:shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
						>
							{player.first_name} {player.last_name}
						</a>
					);
				})}
			</div>
		);
	};

	if (loading) {
		return (
			<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
				<div className="animate-pulse">
					<div className="space-y-3">
						<div className="h-4 bg-gray-200 rounded w-3/4" />
						<div className="h-4 bg-gray-200 rounded w-1/2" />
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
				<div className="text-red-500 text-sm">{error}</div>
			</div>
		);
	}

	if (leagueRosters.length === 0) {
		return (
			<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
				<div className="text-gray-500 text-sm">
					No league rosters found. Please contact support or report an issue.
				</div>
			</div>
		);
	}

	return (
		<React.Fragment>
			<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
				<div className="space-y-8">
					{leagueRosters.map((leagueRoster, index) => {
						// Debug logging
						console.log(`=== RENDERING LEAGUE ROSTER ${index + 1} ===`);
						console.log('Team name:', leagueRoster.team_name);
						console.log('Draft picks:', leagueRoster.draft_picks);
						console.log('Has draft picks:', !!leagueRoster.draft_picks);
						console.log('Draft picks type:', typeof leagueRoster.draft_picks);
						console.log(
							'Draft picks trimmed:',
							leagueRoster.draft_picks ? leagueRoster.draft_picks.trim() : null
						);
						console.log(
							'Will render draft picks section:',
							leagueRoster.draft_picks && leagueRoster.draft_picks.trim() !== ''
						);

						return (
							<div
								key={index}
								className={`pb-6 ${index < leagueRosters.length - 1
									? 'border-b border-gray-200 dark:border-gray-700 mb-6'
									: ''}`}
							>
								{/* Team Header with Avatar, Name, and Button */}
								<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
									<div className="flex items-center gap-4">
										{leagueRoster.image_url && (
											<img
												src={leagueRoster.image_url}
												alt={`${leagueRoster.team_name} logo`}
												className="w-16 h-16 rounded-full object-cover border border-gray-300 dark:border-gray-700 shadow-sm"
												onError={(e) => {
													e.target.style.display = 'none';
												}}
											/>
										)}
										<div>
											<div className="flex items-center gap-2">
												<h4 className="font-bold text-gray-900 dark:text-dark-text text-xl">
													{leagueRoster.team_name}
												</h4>
												{leagueRoster.record && (
													<span className="text-sm font-medium text-gray-600 dark:text-gray-400">
														({leagueRoster.record})
													</span>
												)}
											</div>
											{/* Position Counts - always display under team name */}
											<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
												{(leagueRoster.quarterbacks &&
													leagueRoster.quarterbacks.length) ||
													0}{' '}
												QBs •{' '}
												{(leagueRoster.running_backs &&
													leagueRoster.running_backs.length) ||
													0}{' '}
												RBs •{' '}
												{(leagueRoster.wide_receivers &&
													leagueRoster.wide_receivers.length) ||
													0}{' '}
												WRs •{' '}
												{(leagueRoster.tight_ends &&
													leagueRoster.tight_ends.length) ||
													0}{' '}
												TEs
											</p>
										</div>
									</div>
									{/* Button - positioned to the right on larger screens */}
									<button
										onClick={() => handleAiTradeIdeasClick(leagueRoster.id)}
										className="hidden md:flex bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 items-center shadow-sm hover:shadow-md hover:scale-105 whitespace-nowrap"
									>
										View AI trade insights
									</button>
								</div>

								{/* AI Trade Insights Button - Full Width on small screens */}
								<button
									onClick={() => handleAiTradeIdeasClick(leagueRoster.id)}
									className="md:hidden w-full bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md mb-6"
								>
									View AI trade insights
								</button>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
									<div>
										<h5 className="text-sm font-bold text-gray-800 dark:text-gray-300 mb-3 uppercase tracking-wide">
											Quarterbacks
										</h5>
										{renderPlayers(leagueRoster.quarterbacks, 'QBs')}
									</div>
									<div>
										<h5 className="text-sm font-bold text-gray-800 dark:text-gray-300 mb-3 uppercase tracking-wide">
											Running Backs
										</h5>
										{renderPlayers(leagueRoster.running_backs, 'RBs')}
									</div>
									<div>
										<h5 className="text-sm font-bold text-gray-800 dark:text-gray-300 mb-3 uppercase tracking-wide">
											Wide Receivers
										</h5>
										{renderPlayers(leagueRoster.wide_receivers, 'WRs')}
									</div>
									<div>
										<h5 className="text-sm font-bold text-gray-800 dark:text-gray-300 mb-3 uppercase tracking-wide">
											Tight Ends
										</h5>
										{renderPlayers(leagueRoster.tight_ends, 'TEs')}
									</div>
								</div>

								{/* Draft Picks Section */}
								{leagueRoster.draft_picks &&
								leagueRoster.draft_picks.trim() !== '' && (
									<div className="mt-6">
										<h5 className="text-sm font-bold text-gray-800 dark:text-gray-300 mb-3 uppercase tracking-wide">
											Draft Picks
										</h5>
										<div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
											<p className="text-sm text-gray-700 dark:text-gray-300">
												{leagueRoster.draft_picks}
											</p>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* AI Trade Ideas Modal */}
			<RosterAiTradeIdeasModal
				isOpen={tradeIdeasModalOpen}
				onClose={handleCloseTradeIdeasModal}
				isLoading={tradeIdeasLoading}
				tradeIdeas={tradeIdeas}
				step={modalStep}
				onTradeTypeSelect={handleTradeTypeSelect}
				onPlayerSelectionComplete={handlePlayerSelectionComplete}
				onLeaguePlayerSelectionComplete={handleLeaguePlayerSelectionComplete}
				onTradeAnalyzerComplete={handleTradeAnalyzerComplete}
				userRosterPlayers={userRosterPlayers}
				leagueRosterPlayers={leagueRosterPlayers}
				selectedPlayers={selectedPlayers}
				conversationHistory={conversationHistory}
				onSendChatMessage={handleSendChatMessage}
			/>
		</React.Fragment>
	);
};

export default LeagueRostersCard;
